import { useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import EchoSidebar from './EchoSidebar';
import EchoTopbar from './EchoTopbar';
import AppChatbot from '../shared/AppChatbot';
import GuidedDemoModal from '../shared/GuidedDemoModal';
import { Toaster } from '../ui/sonner';
import { useAppMode } from '../../contexts/AppModeContext';

/* ── Demo chatbot fixtures (only invoked when isDemo === true; see callsite below) ── */
const DEMO_SCHEDULER_PROMPTS = [
  "Fill today's open slots",
  'Show no-show risks',
  'Move Emily Davis to 2 PM',
  'Who can I add to the waitlist?',
];
const DEMO_SCHEDULER_RESPONSE = (msg) => {
  const lower = msg.toLowerCase();
  if (lower.includes('fill') || lower.includes('open'))
    return "**1 open slot today** (11:30 AM with Dr. Chen).\n\n Top 3 waitlist matches:\n- Sarah Chen (Follow-up) — 88% arrival probability\n- Mark Lee (Wellness) — 74%\n- Priya Patel (Sick Visit) — 69%\n\nWant me to send offers?";
  if (lower.includes('no-show') || lower.includes('risk'))
    return "**2 high-risk patients today:**\n- David Wilson (2 PM) — **42% risk**\n- James Taylor (11 AM) — **35% risk**\n\nReminders already sent. Add a call nudge?";
  if (lower.includes('move') || lower.includes('reschedule'))
    return "I can move appointments in one tap. Tell me patient + new time, e.g. *'Move John Smith to 3 PM'*.";
  if (lower.includes('waitlist'))
    return "**Waitlist: 6 patients**\n- 2 flagged urgent\n- 3 flexible\n- 1 needs insurance confirmation\n\nOpen the Waitlist tab for the full ranked list.";
  if (lower.includes('collision') || lower.includes('double') || lower.includes('overbook'))
    return "**1 collision at 10 AM (Dr. Chen)** — Robert Johnson + Sarah Chen both present. I've already drafted a resolution: book Sarah at 1 PM + $10 goodwill credit. Apply?";
  return "I'm your Scheduling agent. Try:\n- *Fill today's open slots*\n- *Show no-show risks*\n- *Move Emily Davis to 2 PM*\n- *Who can I add to the waitlist?*";
};

/* ── General Echo chatbot ── */
const DEMO_ECHO_RESPONSE = (msg) => {
  const lower = msg.toLowerCase();
  if (lower.includes('schedule') || lower.includes('appointment') || lower.includes('today'))
    return "**Today's Schedule (8 patients):**\n\n- 9:00 AM — John Smith (Follow-up) — **Checked in**\n- 9:30 AM — Maria Garcia (New Patient) — **In Exam**\n- 10:00 AM — Robert Johnson (Sick Visit) — **Waiting**\n- 10:30 AM — Emily Davis (Follow-up) — Scheduled\n- 11:00 AM — James Taylor (Wellness) — Scheduled (**35% no-show risk**)\n- 1:00 PM — Sarah Williams (Follow-up) — Scheduled\n- 2:00 PM — David Wilson (New Patient) — Scheduled (**42% no-show risk**)\n- 3:00 PM — Lisa Brown (Sick Visit) — Scheduled\n\n3 in clinic, 5 upcoming. 2 patients have high no-show risk.";
  if (lower.includes('no-show') || lower.includes('no show') || lower.includes('risk'))
    return "**No-Show Risk Analysis:**\n\n- David Wilson (2:00 PM) — **42% risk** (history of 3 no-shows)\n- James Taylor (11:00 AM) — **35% risk** (missed last appointment)\n- Emily Davis (10:30 AM) — **22% risk** (first-time reschedule)\n\nThe Follow-Up Agent has already sent reminders. Want to escalate with a phone call?";
  if (lower.includes('agent') || lower.includes('bot') || lower.includes('ai'))
    return "**AI Agent Status:**\n\n- **Front Desk Agent** — Active (Autopilot) — 142 tasks, 1 credit/task\n- **Intake Agent** — Active (Assist) — 89 tasks, 1 credit/task\n- **Follow-Up Agent** — Active (Autopilot) — 256 tasks, 1 credit/task\n- **Revenue Agent** — Paused — 67 tasks, 1 credit/task\n\nTotal credits used today: **554**. Go to AI Agents to edit flows and settings.";
  if (lower.includes('lens') || lower.includes('upload') || lower.includes('scan'))
    return "**Lens** can process:\n\n- Patient ID cards (1 credit)\n- Insurance cards (1 credit)\n- Referral documents (1 credit)\n- Patient schedules (2 credits)\n- Faxes / PDFs (1 credit)\n- Screenshots (1 credit)\n\nAfter extraction, you can create profiles and schedule appointments directly.";
  if (lower.includes('workflow') || lower.includes('automation'))
    return "Workflows are now managed inside each **AI Agent**. Go to AI Agents → select an agent → Workflows tab.\n\nEach agent has customizable IF/THEN workflows. The Front Desk Agent also has voice settings (tone, speed, language) and an editable conversation flow.";
  if (lower.includes('credit'))
    return "**Credit Costs in Echo:**\n\n- Lens scan: **1-2 credits** per document\n- AI Agent task: **1 credit** per task\n- Voice call (Front Desk): **1 credit** per call\n\nView your balance in the topbar. Purchase more from the credits lightbox.";
  if (lower.includes('assist') || lower.includes('autopilot') || lower.includes('mode'))
    return "**Assist Mode:** AI suggests actions and drafts responses, but waits for your approval before executing.\n\n**Autopilot Mode:** AI executes tasks automatically — answers calls, sends reminders, processes intake. You can override any action.\n\nToggle between modes from the dashboard or per-agent in AI Agents.";
  return "I'm your Echo AI assistant. I can help with:\n\n- **Today's schedule** and patient flow\n- **No-show predictions** and reminders\n- **AI Agent** status and workflows\n- **Lens** document scanning\n- **Credit usage** info\n- **Assist vs Autopilot** modes\n\nWhat would you like to know?";
};

const DEMO_ECHO_PROMPTS = [
  "What's today's schedule?",
  'Show no-show risks',
  'AI agent status',
  'How does Lens work?',
];

// ── Real-mode chatbot — no fabricated patient names; routes the user to
// neutral copy until the chatbot LLM is wired to a backend. ──
const REAL_PROMPTS = [
  'How do I open the schedule?',
  'How do AI agents work?',
  'How does Lens work?',
];
const getRealEchoResponse = (msg) => {
  const lower = (msg || '').toLowerCase();
  if (lower.includes('lens') || lower.includes('upload') || lower.includes('scan'))
    return '**Lens** can process patient ID cards, insurance cards, referral documents, schedules, faxes, and screenshots. Open the Lens upload from any toolbar to get started.';
  if (lower.includes('agent') || lower.includes('bot') || lower.includes('ai'))
    return 'Your AI Agents (Front Desk, Intake, Follow-Up, Revenue) are configured under **AI Agents**. Each agent has Autopilot/Assist modes and editable workflows.';
  if (lower.includes('credit'))
    return 'Credits are consumed per AI action. Track usage in the Credits page; auto top-up can be enabled from Settings.';
  return 'I can help with scheduling, AI agents, Lens, and credits. Ask me about any of those, or open the matching page from the sidebar.';
};

const EchoLayout = () => {
  const { isDemo } = useAppMode();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const location = useLocation();
  const onScheduler = location.pathname.startsWith('/echo/scheduler');

  return (
    <div className="min-h-screen bg-slate-50" data-testid="echo-layout">
      <EchoTopbar sidebarCollapsed={sidebarCollapsed} onStartTour={() => setShowDemo(true)} />
      <EchoSidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
      
      <main className={`pt-14 min-h-screen transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        <div className="p-6 pb-20">
          <Outlet />
        </div>
      </main>

      <AppChatbot
        appName={onScheduler ? 'Echo Scheduling' : 'Echo'}
        subtitle={onScheduler ? 'Ask me to fill slots, reschedule, or check no-show risks.' : 'Ask me about scheduling, patients, agents, or front-office workflows.'}
        getResponse={!isDemo ? getRealEchoResponse : (onScheduler ? DEMO_SCHEDULER_RESPONSE : DEMO_ECHO_RESPONSE)}
        suggestedPrompts={!isDemo ? REAL_PROMPTS : (onScheduler ? DEMO_SCHEDULER_PROMPTS : DEMO_ECHO_PROMPTS)}
        tooltipText={onScheduler ? "I'm your Scheduling agent" : null}
      />
      <GuidedDemoModal app="echo" open={showDemo} onOpenChange={setShowDemo} />
      <Toaster position="top-right" />
    </div>
  );
};

export default EchoLayout;
